<template>
  <div>
    <h1 class="text-h1 mb-8">{{ headerText }}</h1>
    <p class="text-body mb-32">
      {{ subheaderText }}
    </p>
    <form id="corporateSignUpForm" novalidate @submit="onSubmit">
      <div v-for="(field, i) in corporateClient.customFields" :key="field.name">
        <Dropdown
          v-if="field.options && field.options.length"
          :id="`customfield-${i}`"
          ref="campusSelection"
          v-model="signUpForm[field.name]"
          :name="field.name"
          :labelName="field.displayName"
          :options="field.options.map((option) => ({ value: option, display: option }))"
          :status="signUpFormStatuses[field.name]"
          :helperText="helperText(signUpFormStatuses[field.name], field.name, signUpForm[field.name], 'customSelection')"
          class="mt-16"
        />
        <TextField
          v-else
          :id="`customfield-${i}`"
          v-model="signUpForm[field.name]"
          :labelName="field.displayName"
          :name="field.name"
          :status="signUpFormStatuses[field.name]"
          :helperText="helperText(signUpFormStatuses[field.name], field.name, signUpForm[field.name], 'customText')"
          type="text"
          class="mt-16"
        />
      </div>
      <TextField
        id="given-name"
        v-model="signUpForm.first_name"
        labelName="First name"
        name="first_name"
        type="text"
        class="mt-16"
        autocomplete="given-name"
        :status="signUpFormStatuses.first_name"
        :helperText="helperText(signUpFormStatuses.first_name, 'first_name')"
      />
      <TextField
        id="last-name"
        v-model="signUpForm.last_name"
        labelName="Last name"
        name="last_name"
        type="text"
        class="mt-16"
        autocomplete="family-name"
        :status="signUpFormStatuses.last_name"
        :helperText="helperText(signUpFormStatuses.last_name, 'last_name')"
      />
      <TextField
        id="email"
        v-model="signUpForm.email"
        labelName="Email"
        name="email"
        type="email"
        class="mt-16"
        autocomplete="email"
        :status="signUpFormStatuses.email"
        :helperText="helperText(signUpFormStatuses.email, 'email', signUpForm.email)"
      />
      <PasswordTextField
        id="password"
        ref="passwordTextField"
        v-model="signUpForm.password"
        labelName="Password"
        name="password"
        class="mt-16"
        :status="signUpFormStatuses.password"
        :helperText="helperText(signUpFormStatuses.password, 'password', signUpForm.password)"
        @blur="updatePasswordStatus"
      />
      <SearchComboBox
        id="location"
        ref="location"
        name="location"
        :results="dropdownLocationOptions"
        type="search"
        labelName="ZIP code"
        :value="signUpForm.location"
        class="mt-16"
        errorText="ZIP code not found"
        :helperText="helperText(signUpFormStatuses.location, 'location', signUpForm.location)"
        :status="signUpFormStatuses.location"
        @selected="updateValue"
        @valueUpdated="zipToAddress"
        @changingInput="resetLocationErrorState"
        @errorStateChanged="onErrorStateChanged"
      />
      <TextField id="lead_id" class="mt-16 hidden" type="text" :labelName="leadid ? leadid : 'leadid'" />
      <Checkbox
        v-model="signUpForm.accept_terms_and_conditions"
        name="accept_terms_and_conditions"
        checkboxId="terms-of-service"
        class="mt-24 mb-24"
        labelName=""
        labelClass=""
        :formattedLabelText="`I agree to the <a target='_blank' href='${termsOfUseLink}' class='text-grey-clickable underline sign-up-form-link'>Terms of Use</a>. For details on collection and use of your information, see our <a target='_blank' href='${privacyPolicyLink}' class='text-grey-clickable underline sign-up-form-link'>Privacy Policy</a>.`"
        :status="signUpFormStatuses.accept_terms_and_conditions"
        :helperText="helperText(signUpFormStatuses.accept_terms_and_conditions, 'accept_terms_and_conditions')"
      />
      <Button
        id="signUpFormSubmit"
        :loading="isButtonLoading"
        class="w-full"
        variant="primary"
        :value="buttonText"
        size="medium"
        type="submit"
      />
    </form>
    <generic-sign-up-error
      :showError="showError"
      :errorPopupText="errorPopupText"
      :ssoAccountExistsError="ssoAccountExistsError"
      @close="closeErrorPopup"
    >
      <p class="text-body mb-16">
        An account with this email exists. Please
        <a href="https://bhlogin.brighthorizons.com/Account/SingleSignOnService?benefitId=16" class="underline"
          >login with your Bright Horizons account</a
        >
        to access Sittercity.
      </p>
    </generic-sign-up-error>
  </div>
</template>

<script lang="ts">
import Axios from 'axios';
import Button from '@/components/button/Button.vue';
import Checkbox from '@/components/checkbox/Checkbox.vue';
import Dropdown from '@/components/dropdown/Dropdown.vue';
import TextField from '@/components/textfield/TextField.vue';
import SearchComboBox from '@/components/search_combo_box/SearchComboBox.vue';
import PasswordTextField from '@/components/passwordTextField/PasswordTextField.vue';
import helperStatus from '../../../helpers/status';
import { validateEmail } from '../../../helpers/emailHelper';
import GenericSignUpError from './GenericSignUpError.vue';

export default {
  name: 'SignUpForm',
  components: { Button, Checkbox, Dropdown, TextField, PasswordTextField, GenericSignUpError, SearchComboBox },
  props: {
    termsOfUseLink: String!,
    privacyPolicyLink: String!,
    isYearsAhead: Boolean,
    slug: String!,
    corporateClient: Object!,
    logInLink: String!,
    leadid: {
      type: String,
      default: null,
    },
  },
  data(): any {
    return {
      dropdownLocationOptions: [],
      signUpForm: {
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        location: '',
        accept_terms_and_conditions: false,
      },
      signUpFormStatuses: {
        first_name: helperStatus.Neutral,
        last_name: helperStatus.Neutral,
        email: helperStatus.Neutral,
        password: helperStatus.Neutral,
        location: helperStatus.Neutral,
        accept_terms_and_conditions: helperStatus.Neutral,
      },
      serverErrorMessages: {},
      standardFields: ['first_name', 'last_name', 'email', 'password', 'location', 'accept_terms_and_conditions'],
      isButtonLoading: false,
      showError: false,
      errorPopupText: null,
      ssoAccountExistsError: false,
    };
  },
  computed: {
    buttonText() {
      return this.isYearsAhead ? 'Continue' : 'Sign up for free';
    },
    headerText() {
      return this.isYearsAhead ? 'Find Senior Care with Years Ahead' : 'Use your free Sittercity membership';
    },
    subheaderText() {
      return this.isYearsAhead
        ? 'Use the needs assessment and gain access to the resource library and dynamic search tools to get the right care for your family'
        : `${this.corporateClient.name} is here to support your needs. Sign up to activate your free membership ($180/year\xa0value) and connect directly with care near you.`;
    },
    searchComboBoxError() {
      return this.signUpFormStatuses.location === helperStatus.Error;
    },
    requiredFields() {
      const fields = ['first_name', 'last_name', 'email', 'password', 'location', 'accept_terms_and_conditions'];
      this.corporateClient.customFields.forEach((field) => {
        if (field.required) fields.push(field.name);
      });
      return fields;
    },
    customFieldNames() {
      return this.corporateClient.customFields.map((option) => option.name);
    },
  },
  watch: {
    corporateClient: {
      handler(val) {
        if (val.customFields) {
          val.customFields.forEach((field) => this.$set(this.signUpForm, field.name, ''));
          val.customFields.forEach((field) => this.$set(this.signUpFormStatuses, field.name, helperStatus.Neutral));
        }
      },
      deep: true,
    },
    signUpForm: {
      handler(val) {
        Object.keys(this.signUpFormStatuses).forEach((key) => {
          if (key in this.serverErrorMessages) return;
          if (this.signUpFormStatuses[key] === helperStatus.Error) {
            if (key === 'accept_terms_and_conditions' && this.signUpForm[key]) {
              this.signUpFormStatuses[key] = helperStatus.Neutral;
            } else if (key === 'email') {
              if (validateEmail(val[key])) {
                this.signUpFormStatuses[key] = helperStatus.Neutral;
              }
            } else if (!this.standardFields.includes(key)) {
              const customFieldIndex = this.customFieldNames.indexOf(key);
              const { pattern, required } = this.corporateClient.customFields[customFieldIndex];
              const type = this.corporateClient.customFields[customFieldIndex].__typename;
              if (type === 'SelectField' && val[key]) {
                this.signUpFormStatuses[key] = helperStatus.Neutral;
              }
              if (pattern) {
                const regex = this.regexFromString(pattern);
                if (regex.test(val[key]) || (!required && val[key].length === 0)) {
                  this.signUpFormStatuses[key] = helperStatus.Neutral;
                }
              }
            } else if (val[key].length > 0 && this.requiredFields.includes(key)) {
              this.signUpFormStatuses[key] = helperStatus.Neutral;
            }
          }
        });
      },
      deep: true,
    },
  },
  methods: {
    onErrorStateChanged(hasError) {
      if (hasError) {
        this.signUpFormStatuses.location = helperStatus.Error;
      }
    },
    resetLocationErrorState(): void {
      this.signUpFormStatuses.location = helperStatus.Neutral;
    },
    closeErrorPopup() {
      this.showError = false;
      this.ssoAccountExistsError = false;
    },
    updateValue(v): void {
      this.signUpForm.location = v.value;
    },
    updatePasswordStatus(): void {
      if (this.$refs.passwordTextField.allRequirementsMet) {
        this.signUpFormStatuses.password = helperStatus.Neutral;
      } else {
        this.signUpFormStatuses.password = helperStatus.Error;
      }
    },
    async zipToAddress(zipcode: string) {
      this.signUpForm.location = zipcode;
      if (zipcode && zipcode.length) {
        await Axios.get(`${window.location.origin}/suggest?location=${zipcode}`).then((response) => {
          const options = response.data.map((entry) => ({ display: entry.label, value: entry.value }));
          this.dropdownLocationOptions = options;
        });
      }
    },
    regexFromString(string) {
      const parts = /\/(.*)\/(.*)/.exec(string);
      return new RegExp(parts[1], parts[2]);
    },
    async onSubmit(e) {
      Object.keys(this.serverErrorMessages).forEach((key) => {
        this.signUpFormStatuses[key] = helperStatus.Neutral;
      });
      this.serverErrorMessages = {};
      e.preventDefault();

      // eslint-disable-next-line no-restricted-syntax
      for (const property of Object.keys(this.signUpForm)) {
        const customFieldIndex = this.customFieldNames.indexOf(property);
        if (property === 'accept_terms_and_conditions' && !this.signUpForm[property]) {
          this.signUpFormStatuses[property] = helperStatus.Error;
        } else if (this.signUpForm[property].length === 0 && this.requiredFields.includes(property)) {
          this.signUpFormStatuses[property] = helperStatus.Error;
        } else if (property === 'email' && !validateEmail(this.signUpForm[property])) {
          this.signUpFormStatuses[property] = helperStatus.Error;
        } else if (property === 'location') {
          const regex = /.*, [A-Z]{2} \d{5}/;
          if (!regex.test(this.signUpForm[property])) {
            this.signUpFormStatuses[property] = helperStatus.Error;
          }
        } else if (customFieldIndex !== -1) {
          const { pattern, required } = this.corporateClient.customFields[customFieldIndex];
          const mustCheck = required || this.signUpForm[property].length; // we have to check the regex if the field is required or if the field has anything entered
          if (pattern && mustCheck) {
            const regex = this.regexFromString(pattern);
            if (!regex.test(this.signUpForm[property])) {
              this.signUpFormStatuses[property] = helperStatus.Error;
            }
          }
        }
      }
      this.updatePasswordStatus();

      const hasAnyError = Object.values(this.signUpFormStatuses).includes(helperStatus.Error);

      if (!hasAnyError) {
        this.errorPopupText = null;
        this.isButtonLoading = true;
        const url = `/${this.slug}/bh-benefits-signup`;
        const formData = new FormData(e.target);
        formData.append('location', this.signUpForm.location);
        formData.append('accept_terms_and_conditions', this.signUpForm.accept_terms_and_conditions ? '1' : '0');
        formData.append('page_name', 'create_seeker');
        formData.append('site', this.isYearsAhead ? 'years_ahead' : 'sittercity');
        formData.append('member_type', 'seeker');
        formData.append('leadid', this.leadid);
        await Axios.post(url, formData)
          .then((response) => {
            const { redirectTo } = response.data;
            window.location = redirectTo;
          })
          .catch((err) => {
            if (err && err.response && err.response.data && err.response.data.errors) {
              const { errors } = err.response.data;
              if (errors.zip_code) errors.location = errors.location.concat(errors.zip_code);
              Object.keys(errors).forEach((key) => {
                if (errors[key]) {
                  this.signUpFormStatuses[key] = helperStatus.Error;
                }
              });
              if (errors.email === null || !Object.keys(errors).length) {
                this.showError = true;
              }
              if (errors.eligibility_errors) {
                this.errorPopupText =
                  "The information you entered doesn't match the information on file for your employer. Review the information to ensure it matches. If you are still having trouble, contact us or your employer's benefits administrator.";
                this.showError = true;
              }
              if (errors.sso_account_exists) {
                this.ssoAccountExistsError = true;
                this.showError = true;
              }
              if (errors.sso_internal_error) {
                this.showError = true;
              }
              this.serverErrorMessages = err.response.data.errors;
            }
            this.isButtonLoading = false;
          });
      }
    },
    helperText(status: helperStatus, type: string, value: string, customFieldType?: string) {
      if (this.serverErrorMessages && this.serverErrorMessages[type] && this.serverErrorMessages[type].length) {
        const formattedErrors = this.serverErrorMessages[type].join(' <br/> ');
        return formattedErrors;
      }
      // Error helper text
      if (status === helperStatus.Error) {
        if (customFieldType === 'customSelection') {
          return 'Select a valid option';
        }
        if (customFieldType === 'customText' && value.length > 0) {
          const customFieldNames = this.corporateClient.customFields.map((option) => option.name);
          const customFieldIndex = customFieldNames.indexOf(type);
          const { pattern } = this.corporateClient.customFields[customFieldIndex];
          if (!new RegExp(pattern, 'g').test(value)) {
            return 'Enter a valid value';
          }
        }
        if (type === 'location' && value.length > 0) {
          const regex = /.*, [A-Z]{2} \d{5}/;
          if (!new RegExp(regex, 'g').test(value)) {
            return 'Format your location as City, State ZIP';
          }
        }
        if (type === 'accept_terms_and_conditions') {
          return 'You must agree to the terms of service';
        }
        if (type === 'email' && value.length > 0 && !validateEmail(value)) {
          return 'Enter a valid email';
        }
        if (this.requiredFields.includes(type)) {
          return 'This field is required';
        }
      }
      // Non error state helper text
      if (type === 'password') {
        return '6 character minimum';
      }
      if (type === 'location') {
        return 'Select your location from the dropdown';
      }
      return '';
    },
  },
};
</script>
